import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Avatar,
  Badge,
  Box,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';

import { BlockedContactName } from 'components/BlockedContactName';
import CardContextMenu from 'components/CardContextMenu';
import CardItem from 'components/CardItem';
import FilterListaAtendimentos from 'components/FilterListaAtendimentos';
import IncomingTransferIndicator from 'components/IncomingTransferIndicator';
import OfficialWhatsAppIndicator from 'components/OfficialWhatsAppIndicator';
import SearchBar from 'components/SearchBar';
import TagsV2 from 'components/TagsV2/TagsV2';
import VirtualizedListContainer from 'components/VirtualizedListContainer';
import { LOCAL_STORAGE_KEY_ORDER_BY_ATENDIMENTOS_INICIADOS } from 'constants/localStorageKeys';
import * as socketEvents from 'constants/socketEvents';
import useCardContextMenu from 'hooks/useCardContextMenu';
import useCurrentUser from 'hooks/useCurrentUser';
import { useDebouncedFetch } from 'hooks/useDebouncedFetch';
import { usePersistedOrderBy } from 'hooks/usePersistedOrderBy';
import { OrderByOption } from 'hooks/useUrlFilters';
import dayjs from 'services/dayjs';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoInterface,
  atendimentosSelector,
  AtendimentoStatusInterface,
  fetchAtendimentosIniciados,
  fetchMarcarAtendimentoComoLido,
  fetchMarcarAtendimentoComoNaoLido,
  resetMostrarAlertaDeInatividade,
  setAtendimentoSelecionado,
  setMarcarAtendimentoComoLido,
  setMarcarAtendimentoComoNaoLido,
  setTotalMensagensNaoLidasAtendimentoIniciado,
  updateListaDeIniciadosBloqueioContato,
  fetchMarcarTodosAtendimentosComoLidos,
  setMarcarTodosAtendimentosComoLidos,
  AtendimentoFlagType,
  setLimparTransferenciaRecebida,
  AtendimentoFlagEnum,
  clearFlagAtendimento,
} from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';
import {
  ContarNovaMensagemInterface,
  mensagensSelector,
} from 'store/reducers/mensagens';
import getIconTipoCanal from 'utils/getIconTipoCanal';

import AlertaIndisponibilidade from './AlertaIndisponibilidade';
import ButtonFixarAtendimento from './ButtonFixarAtendimento';
import CronometroSessaoAtendimento from './CronometroSessaoAtendimento';

const ONE_MINUTE_IN_MILLISECONDS = 60000; // 1min
const MAX_DEBOUNCE_TIME = 30; // 30 segundos

const useStyles = makeStyles(
  ({ spacing, palette, breakpoints, typography }) => ({
    grid: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    list: {
      height: '100%',
      overflowY: 'auto',
      paddingRight: spacing(1),
      marginBottom: spacing(1),
    },
    fixarButton: {
      opacity: 0,
      transition: 'all 0.1s ease',
    },
    fixarButtonVisible: {
      opacity: 1,
    },
    listItem: {
      '&:hover $fixarButton': {
        opacity: 1,
      },
    },
    cardItem: {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    avatar: {
      width: typography.fontSize * 3.33,
      height: typography.fontSize * 3.33,
    },
    badge: {
      position: 'relative',
      transform: 'scale(1) translate(0, 0)',
    },
    badgeAnimate: {
      boxShadow: `0 0 0 2px ${palette.background.paper}`,
      '&::after': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        animation: '$ripple 1.2s infinite ease-in-out',
        border: '1px solid currentColor',
        content: '""',
      },
    },
    '@keyframes ripple': {
      '0%': {
        transform: 'scale(.8)',
        opacity: 1,
      },
      '100%': {
        transform: 'scale(2.4)',
        opacity: 0,
      },
    },
    tagsContainer: {
      display: 'flex',
      justifyContent: 'center',

      [breakpoints.up('lg')]: {
        justifyContent: 'center',
      },
    },
    fixarContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-end',
    },
  }),
);

interface ListaAtendimentosIniciadosVirtualizadaProps {
  resetSelectedFiles: () => void;
}

export default function ListaAtendimentosIniciadosVirtualizada({
  resetSelectedFiles,
}: ListaAtendimentosIniciadosVirtualizadaProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const theme = useTheme();
  const currentUser = useCurrentUser();
  const { typography } = theme;
  const [animateBadge, setAnimateBadge] = useState(false);
  const [temposEmAtendimento, setTemposEmAtendimento] = useState<
    Record<string, string>
  >({});
  const dispatch = useAppDispatch();
  const { loadingMensagensAtendimento } = useAppSelector(mensagensSelector);
  const {
    atendimentoSelecionado,
    atendimentosIniciados,
    loadingAtendimentosIniciados,
  } = useAppSelector(atendimentosSelector);

  const parentRef = useRef<HTMLUListElement>(null);
  const atendimentosIniciadosRef = useRef(atendimentosIniciados);
  atendimentosIniciadosRef.current = atendimentosIniciados;

  const virtualizer = useVirtualizer({
    count: atendimentosIniciados.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 150,
    measureElement: el => el?.getBoundingClientRect().height,
    getItemKey: index => atendimentosIniciados[index]?.id ?? index,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const tempoDoDebounceRef = useRef(0); // VERSÃO NOVA
  const isInitialMount = useRef(true);

  const [debounceIntervalId, setDebounceIntervalId] =
    useState<NodeJS.Timeout | null>(null); // VERSÃO NOVA
  const [estaContando, setEstaContando] = useState(false); // VERSÃO NOVA

  const handleSelect = (atendimento: AtendimentoInterface) => {
    if (atendimento.id === atendimentoSelecionado?.id) return;

    if (!loadingMensagensAtendimento) {
      resetSelectedFiles();
      dispatch(setAtendimentoSelecionado(atendimento));

      if (atendimento.flag) {
        dispatch(clearFlagAtendimento(atendimento.id));
      }
    }
  };

  const { orderBy, setOrderBy: persistOrderBy } = usePersistedOrderBy(
    LOCAL_STORAGE_KEY_ORDER_BY_ATENDIMENTOS_INICIADOS,
    'orderInitiatedBy',
  );

  const [filterOptions, setFilterOptions] = useState<{
    orderBy?: OrderByOption;
    search?: string;
  }>({
    orderBy,
    search: undefined,
  });
  const handleSearch = (value: string) => {
    setFilterOptions(options => ({ ...options, search: value }));

    if (!loadingAtendimentosIniciados) {
      getAtendimentosIniciados({
        search: value,
        orderBy: filterOptions.orderBy,
      });
    }
  };

  const getAtendimentosIniciados = useCallback(
    (params: { search?: string; orderBy?: string }) => {
      const paramsFiltered = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined),
      );

      dispatch(fetchAtendimentosIniciados(paramsFiltered))
        .unwrap()
        .then(atendimentos => {
          const atendimentoSelecionadoId = atendimentoSelecionado?.id;
          if (atendimentoSelecionadoId) {
            const atendimentoAtualizado = atendimentos.find(
              a => a.id === atendimentoSelecionadoId,
            );

            if (atendimentoAtualizado) {
              dispatch(setAtendimentoSelecionado(atendimentoAtualizado));
            } else {
              dispatch(setAtendimentoSelecionado());
            }
          }
        });
    },
    [dispatch, atendimentoSelecionado?.id],
  );

  const handleSetOrderBy = useCallback(
    (orderByValue: OrderByOption) => {
      setFilterOptions(options => ({ ...options, orderBy: orderByValue }));
      persistOrderBy(orderByValue);

      if (!loadingAtendimentosIniciados) {
        getAtendimentosIniciados({
          search: filterOptions.search,
          orderBy: orderByValue,
        });
      }
    },
    [
      setFilterOptions,
      persistOrderBy,
      loadingAtendimentosIniciados,
      getAtendimentosIniciados,
      filterOptions.search,
    ],
  );

  const fetchAtendimentosIniciadosComOrderBy = useCallback(() => {
    setFilterOptions(options => ({ ...options, orderBy }));
    return dispatch(fetchAtendimentosIniciados({ orderBy }));
  }, [dispatch, orderBy, setFilterOptions]);

  const handleCancelSearch = () => {
    setFilterOptions(options => ({ ...options, search: undefined }));

    if (!loadingAtendimentosIniciados) {
      getAtendimentosIniciados({
        orderBy: filterOptions.orderBy,
      });
    }
  };
  // VERSÃO NOVA
  const executeImmediateFetch = useCallback(() => {
    if (debounceIntervalId) {
      clearInterval(debounceIntervalId);
      setDebounceIntervalId(null);
    }
    setEstaContando(false);
    tempoDoDebounceRef.current = 0;

    if (!animateBadge) setAnimateBadge(true);

    const timeout = setTimeout(() => {
      setAnimateBadge(false);
    }, 3000);

    fetchAtendimentosIniciadosComOrderBy();

    return () => clearTimeout(timeout);
  }, [debounceIntervalId, animateBadge, fetchAtendimentosIniciadosComOrderBy]);

  let timeout: NodeJS.Timeout;

  const fetchAtendimentosIniciadosComDebounce = useDebouncedCallback(() => {
    if (debounceIntervalId) {
      clearInterval(debounceIntervalId);
      setDebounceIntervalId(null);
    }

    setEstaContando(false);
    tempoDoDebounceRef.current = 0;

    if (!animateBadge) setAnimateBadge(true);

    timeout = setTimeout(() => {
      setAnimateBadge(false);
    }, 3000);

    fetchAtendimentosIniciadosComOrderBy();
  }, 3000);

  // Debounce para eventos de status: evita múltiplos fetches quando chegam
  // vários eventos com atendimentos diferentes ao mesmo tempo.
  // Callbacks acumulados na janela do debounce são todos executados após o fetch.
  const agendarFetchPorStatus = useDebouncedFetch(
    () => fetchAtendimentosIniciadosComOrderBy(),
    100,
  );

  const handleNovaMensagemComContador = useCallback(
    (data: ContarNovaMensagemInterface) => {
      const { atendimentoId, posicao, totalNaoLidas: total } = data;

      if (posicao !== 'ATENDENTE') return;

      const estaEmIniciados = atendimentosIniciadosRef.current.some(
        a => a.id === atendimentoId,
      );
      if (!estaEmIniciados) return;

      // Eventos de mudança de estado de leitura (total === -1 ou total === 0):
      // atualizar badge diretamente sem debounce/refetch, pois não são
      // novas mensagens reais, apenas mudanças de estado
      if (total === -1 || total === 0) {
        dispatch(
          setTotalMensagensNaoLidasAtendimentoIniciado({
            atendimentoId,
            total,
          }),
        );
        return;
      }

      if (atendimentoId !== atendimentoSelecionado?.id) {
        if (!estaContando) {
          tempoDoDebounceRef.current = 0;
          setEstaContando(true);

          const id = setInterval(() => {
            const next = tempoDoDebounceRef.current + 1;

            if (next >= MAX_DEBOUNCE_TIME) {
              executeImmediateFetch();
              tempoDoDebounceRef.current = 0;
              return;
            }

            tempoDoDebounceRef.current = next;
          }, 1000);

          setDebounceIntervalId(id);
        }

        fetchAtendimentosIniciadosComDebounce();
        return;
      }

      dispatch(
        setTotalMensagensNaoLidasAtendimentoIniciado({ atendimentoId, total }),
      );
    },
    [
      atendimentoSelecionado?.id,
      estaContando,
      executeImmediateFetch,
      fetchAtendimentosIniciadosComDebounce,
      dispatch,
    ],
  );

  const handleMarkAsRead = () => {
    confirm({
      title: 'Marcar todos como lidos',
      description:
        'Tem certeza de que deseja marcar todos os atendimentos como lidos?',
      dialogProps: { maxWidth: 'xs', fullWidth: true },
      cancellationText: 'Cancelar',
      confirmationText: 'Ok',
    })
      .then(() => {
        dispatch(fetchMarcarTodosAtendimentosComoLidos())
          .unwrap()
          .then(() => {
            dispatch(setMarcarTodosAtendimentosComoLidos());
            enqueueSnackbar(
              'Todos os atendimentos foram marcados como lidos.',
              {
                variant: 'success',
              },
            );
          })
          .catch(() => {
            enqueueSnackbar(
              'Não foi possível marcar todos como lidos. Tente novamente.',
            );
          });
      })
      .catch(() => undefined);
  };

  // event listeners
  useEffect(() => {
    // VERSÃO ANTIGA let timeout: NodeJS.Timeout;
    // Observa as mudança de status de atendimento que interfere na lista de iniciados
    const handlerAtendimentoStatus = (data: AtendimentoStatusInterface) => {
      const { status, atendimento } = data;

      const atendimentoSelecionadoId = atendimentoSelecionado?.id;

      switch (status) {
        case 'finalizado-por-mim':
        case 'transferido-fila-espera':
        case 'transferido-outro-atendente':
        case 'transferencia-forcada':
          agendarFetchPorStatus(
            atendimento.id === atendimentoSelecionadoId
              ? () => {
                  dispatch(setAtendimentoSelecionado());
                  dispatch(resetMostrarAlertaDeInatividade());
                }
              : undefined,
          );
          break;

        case 'finalizado':
          if (atendimento.posicao === 'ATENDENTE') {
            agendarFetchPorStatus(
              atendimento.id === atendimentoSelecionadoId
                ? () => {
                    dispatch(setAtendimentoSelecionado());
                    dispatch(resetMostrarAlertaDeInatividade());
                  }
                : undefined,
            );
          }

          break;
        case 'fila-espera': {
          // Só atualiza a lista de iniciados se o atendimento já estava nela.
          // Evita fetch desnecessário quando é uma nova entrada na fila de espera
          // (atendimento que nunca estava em iniciados, ex: contato chegando do zero).
          const estaEmIniciados = atendimentosIniciadosRef.current.some(
            a => a.id === atendimento.id,
          );
          if (estaEmIniciados) {
            agendarFetchPorStatus(
              atendimento.id === atendimentoSelecionadoId
                ? () => {
                    dispatch(setAtendimentoSelecionado());
                    dispatch(resetMostrarAlertaDeInatividade());
                  }
                : undefined,
            );
          }
          break;
        }
        case 'iniciado-por-mim':
          fetchAtendimentosIniciadosComOrderBy();
          break;
        default:
          break;
      }
    };
    // VERSÃO ANTIGA // Monitora e contabiliza as mensagens recebidas dos atendimentos inciados
    // const handleContarMensagem = (data: ContarNovaMensagemInterface) => {
    //   const { atendimentoId, posicao, totalNaoLidas: total } = data;

    //   // ignora a contagem das novas mensagens que nao sejam de um atendimento iniciado
    //   if (posicao !== 'ATENDENTE') return;

    //   // Adiciona uma animação temporária no card do atendimento selecionado
    //   // sempre que uma nova mensagem chegar
    //   if (atendimentoId !== atendimentoSelecionado?.id) {
    //     if (!animateBadge) setAnimateBadge(true);

    //     timeout = setTimeout(() => {
    //       setAnimateBadge(false);
    //     }, 3000);
    //   }

    //   dispatch(
    //     setTotalMensagensNaoLidasAtendimentoIniciado({ atendimentoId, total }),
    //   );
    // };
    // Atualiza a lista de atendimentos iniciados quando reconectar o socket

    const handleReconnect = () => {
      dispatch(
        fetchAtendimentosIniciados({
          orderBy: filterOptions.orderBy,
          search: filterOptions.search,
        }),
      )
        .unwrap()
        // Percorre a lista de atendimentos iniciados e verifica se o atendimento
        // selecionado já não está mais na lista de iniciados e o desseleciona
        .then(atendimentosIniciados => {
          const atendimentoSelecionadoId = atendimentoSelecionado?.id;
          const indexAtendimento = atendimentosIniciados.findIndex(
            ({ id }) => id === atendimentoSelecionadoId,
          );

          if (indexAtendimento < 0) dispatch(setAtendimentoSelecionado());
        });
    };

    const handleContatoBloqueado = (data: {
      contatoId: string;
      bloqueadoEm?: string | null;
    }) => {
      dispatch(
        updateListaDeIniciadosBloqueioContato({
          contatoId: data.contatoId,
          bloqueadoEm: data.bloqueadoEm ?? new Date().toISOString(),
        }),
      );
    };

    const handleContatoDesbloqueado = (data: {
      contatoId: string;
      bloqueadoEm?: string | null;
    }) => {
      dispatch(
        updateListaDeIniciadosBloqueioContato({
          contatoId: data.contatoId,
          bloqueadoEm: data.bloqueadoEm ?? null,
        }),
      );
    };

    const handleLimparFlagAtendimento = (data: {
      atendimentoId: string;
      flag: AtendimentoFlagType;
    }) => {
      dispatch(
        setLimparTransferenciaRecebida({
          atendimentoId: data.atendimentoId,
        }),
      );
    };

    const handleChannelSendModeUpdated = () => {
      getAtendimentosIniciados({
        search: filterOptions.search,
        orderBy: filterOptions.orderBy,
      });
    };

    socket.on(
      socketEvents.ON_CHANNEL_SEND_MODE_UPDATED,
      handleChannelSendModeUpdated,
    );
    socket.on(
      socketEvents.ON_LIMPAR_FLAG_ATENDIMENTO,
      handleLimparFlagAtendimento,
    );
    socket.on(socketEvents.ON_ATENDIMENTO_STATUS, handlerAtendimentoStatus);
    socket.on(
      socketEvents.ON_CONTAR_NOVA_MENSAGEM,
      // handleContarMensagem, VERSÃO ANTIGA
      handleNovaMensagemComContador, // VERSÃO NOVA
    );
    socket.on(socketEvents.ON_RECONNECT, handleReconnect);
    socket.on(socketEvents.ON_CONTATO_BLOQUEADO, handleContatoBloqueado);
    socket.on(socketEvents.ON_CONTATO_DESBLOQUEADO, handleContatoDesbloqueado);

    return () => {
      socket.off(
        socketEvents.ON_CHANNEL_SEND_MODE_UPDATED,
        handleChannelSendModeUpdated,
      );
      socket.off(
        socketEvents.ON_LIMPAR_FLAG_ATENDIMENTO,
        handleLimparFlagAtendimento,
      );
      socket.off(socketEvents.ON_ATENDIMENTO_STATUS, handlerAtendimentoStatus);
      socket.off(
        socketEvents.ON_CONTAR_NOVA_MENSAGEM,
        // handleContarMensagem, VERSÃO ANTIGA
        handleNovaMensagemComContador, // VERSÃO NOVA
      );
      socket.off(socketEvents.ON_RECONNECT, handleReconnect);
      socket.off(socketEvents.ON_CONTATO_BLOQUEADO, handleContatoBloqueado);
      socket.off(
        socketEvents.ON_CONTATO_DESBLOQUEADO,
        handleContatoDesbloqueado,
      );

      clearTimeout(timeout);
      setAnimateBadge(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    atendimentoSelecionado?.id,
    agendarFetchPorStatus,
    dispatch,
    filterOptions.orderBy,
    filterOptions.search,
    handleNovaMensagemComContador, // VERSÃO NOVA
  ]);

  // VERSÃO NOVA
  useEffect(() => {
    return () => {
      if (debounceIntervalId) {
        clearInterval(debounceIntervalId);
      }
    };
  }, [debounceIntervalId]);

  // carrega as tags do contato do atendimento
  // Carrega os atendimentos e iniciados e percorre a lista para contar as mensagens nao lidas
  useEffect(() => {
    if (isInitialMount.current) {
      fetchAtendimentosIniciadosComOrderBy();
      isInitialMount.current = false;
    }

    return () => {
      dispatch(setAtendimentoSelecionado());
    };
  }, [dispatch, fetchAtendimentosIniciadosComOrderBy]);

  // Atualiza o tempo em atendimento de cada atendimento na lista a cada minuto
  useEffect(() => {
    const updateTempoEmAtendimento = () => {
      // Indexa por id: o render usa a janela virtualizada, então a posição no
      // array não corresponde à posição do atendimento na lista.
      const tempos: Record<string, string> = {};
      atendimentosIniciados.forEach(atendimento => {
        const { id, iniciadoEm } = atendimento;
        if (iniciadoEm) {
          tempos[id] = dayjs(iniciadoEm).fromNow();
        }
      });
      setTemposEmAtendimento(tempos);
    };

    updateTempoEmAtendimento();

    const intervalId = setInterval(() => {
      updateTempoEmAtendimento();
    }, ONE_MINUTE_IN_MILLISECONDS);

    return () => {
      clearInterval(intervalId);
    };
  }, [atendimentosIniciados]);

  const {
    contextMenuPosition,
    contextMenuItem: contextMenuAtendimento,
    menuPaperRef,
    handleContextMenuOpen,
    handleContextMenuClose: handleClose,
  } = useCardContextMenu<AtendimentoInterface>();

  const handleMarcarComoNaoLido = (atendimentoId: string) => {
    dispatch(fetchMarcarAtendimentoComoNaoLido(atendimentoId))
      .unwrap()
      .then(() => {
        dispatch(setMarcarAtendimentoComoNaoLido(atendimentoId));
        enqueueSnackbar('Atendimento marcado como não lido.', {
          variant: 'success',
        });
      })
      .catch(() => {
        enqueueSnackbar(
          'Não foi possível marcar como não lido. Tente novamente.',
          {
            variant: 'error',
          },
        );
      })
      .finally(() => {
        handleClose();
      });
  };

  const handleMarcarComoLido = (atendimentoId: string) => {
    dispatch(fetchMarcarAtendimentoComoLido(atendimentoId))
      .unwrap()
      .then(() => {
        dispatch(setMarcarAtendimentoComoLido(atendimentoId));
        enqueueSnackbar('Atendimento marcado como lido.', {
          variant: 'success',
        });
      })
      .catch(() => {
        enqueueSnackbar('Não foi possível marcar como lido. Tente novamente.', {
          variant: 'error',
        });
      })
      .finally(() => {
        handleClose();
      });
  };

  const renderAtendimentosIniciados = virtualItems.map((virtualRow, index) => {
    const atendimento = atendimentosIniciados[virtualRow.index];
    const key = atendimento ? atendimento.id : index;

    // A API pode retornar atendimentos com relacionamentos removidos/nulos.
    // Não deixe um registro inconsistente derrubar toda a lista.
    if (!atendimento?.contato || !atendimento.canal) return null;

    if (atendimento) {
      const { id, contato, canal, totalMensagensNaoLidas, flag } = atendimento;
      const selected = atendimentoSelecionado?.id === id;
      const isOfficialWhatsAppChannel =
        canal.tipoCanal === TipoCanalEnum.WHATSAPP_OFICIAL ||
        (canal.isModoHibrido && canal.modoEnvio === 'API_OFICIAL');
      const isInstagramOrMessengerChannel =
        canal.tipoCanal === TipoCanalEnum.INSTAGRAM ||
        canal.tipoCanal === TipoCanalEnum.MESSENGER;

      const iconSize = canal.isModoHibrido ? 20 : undefined;

      const IconTipoCanal = getIconTipoCanal({
        tipoCanal: canal.tipoCanal,
        size: iconSize,
        isModoHibrido: canal.isModoHibrido,
      });
      const tempoEmAtendimento = temposEmAtendimento[id];
      const tagsDoContato = atendimento.contato.tags;
      const hasMarkedAsUnread = totalMensagensNaoLidas === -1;
      const shouldIncomeTransferIndicator =
        flag === AtendimentoFlagEnum.TRANSFERIDO;

      return (
        <div
          key={key}
          ref={virtualizer.measureElement}
          data-index={virtualRow.index}
        >
          <div onContextMenu={e => handleContextMenuOpen(e, atendimento)}>
            <CardItem
              key={key}
              className={clsx(classes.cardItem)}
              onSelect={() => handleSelect(atendimento)}
              selected={selected}
              isIncomingTransferIndicator={shouldIncomeTransferIndicator}
            >
              <ListItem disableGutters dense className={clsx(classes.listItem)}>
                <ListItemAvatar>
                  <Box mr={1}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      badgeContent={IconTipoCanal}
                    >
                      <Avatar
                        src={contato.foto}
                        className={classes.avatar}
                        style={{
                          width: typography.fontSize * 3.33,
                          height: typography.fontSize * 3.33,
                        }}
                      />
                    </Badge>
                  </Box>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      display="flex"
                      alignItems="baseline"
                      flexWrap="wrap"
                      gridGap={2}
                    >
                      <BlockedContactName
                        name={contato.nome}
                        isBlocked={Boolean(
                          contato.blockedAt || contato.bloqueadoEm,
                        )}
                        style={{ maxWidth: '100%' }}
                      />

                      {contato.contato && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          ({contato.contato})
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography
                        title="Nome do canal"
                        variant="caption"
                        noWrap
                      >
                        {canal.nome}
                      </Typography>
                      <Typography
                        title={`Em atendimento ${tempoEmAtendimento}`}
                        variant="caption"
                        component="div"
                        noWrap
                      >
                        {tempoEmAtendimento}
                      </Typography>
                    </>
                  }
                  primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                  secondaryTypographyProps={{
                    noWrap: true,
                    variant: 'caption',
                  }}
                  style={{ margin: 0 }}
                />

                <Box className={classes.fixarContainer}>
                  <Box>
                    <div
                      className={clsx(classes.fixarButton, {
                        [classes.fixarButtonVisible]: atendimento.fixadoEm,
                      })}
                    >
                      <ButtonFixarAtendimento
                        atendimentoId={atendimento.id}
                        fixed={!!atendimento.fixadoEm}
                      />
                    </div>
                  </Box>

                  <Box
                    onClick={e => e.stopPropagation()}
                    className={classes.tagsContainer}
                  >
                    {/* <Tags
                    isContato
                    tagsAtuais={tagsDoContato}
                    id={atendimento.contato.id}
                    atendimentoId={id}
                  /> */}
                    <TagsV2
                      isContato
                      tags={tagsDoContato}
                      id={atendimento.contato.id}
                      atendimentoId={id}
                    />
                  </Box>
                </Box>

                <Box
                  ml={1}
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  gridGap={12}
                >
                  {id !== atendimentoSelecionado?.id &&
                    totalMensagensNaoLidas > 0 && (
                      <Box>
                        <Badge
                          color="error"
                          overlap="rectangular"
                          classes={{
                            badge: classes.badge,
                            ...(atendimento.id !== atendimentoSelecionado?.id &&
                              animateBadge && { root: classes.badgeAnimate }),
                          }}
                          badgeContent={totalMensagensNaoLidas}
                        />
                      </Box>
                    )}

                  {hasMarkedAsUnread && (
                    <Box>
                      <Badge
                        color="error"
                        overlap="rectangular"
                        classes={{
                          badge: classes.badge,
                        }}
                        badgeContent=""
                      />
                    </Box>
                  )}
                  {/* {atendimento?.transferenciaPendenteId && (
                    <ButtonCancelarTransferencia
                      size="small"
                      atendimentoId={id}
                      transferenciaPendenteId={
                        atendimento?.transferenciaPendenteId
                      }
                    />
                  )} */}
                  {shouldIncomeTransferIndicator && (
                    <IncomingTransferIndicator />
                  )}
                </Box>
              </ListItem>

              {isOfficialWhatsAppChannel && atendimento?.sessaoStatus && (
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <OfficialWhatsAppIndicator />

                  <CronometroSessaoAtendimento atendimento={atendimento} />
                </Box>
              )}

              {isInstagramOrMessengerChannel && (
                <Box display="flex" justifyContent="flex-end">
                  <CronometroSessaoAtendimento atendimento={atendimento} />
                </Box>
              )}
            </CardItem>
          </div>
        </div>
      );
    }

    return (
      <CardItem key={key} disabled>
        <ListItem disableGutters dense>
          <ListItemAvatar>
            <Skeleton variant="circle">
              <Avatar />
            </Skeleton>
          </ListItemAvatar>
          <ListItemText primary={<Skeleton variant="rect" />} />
        </ListItem>
      </CardItem>
    );
  });

  const contextMenuHasUnread =
    (contextMenuAtendimento?.totalMensagensNaoLidas ?? 0) > 0 ||
    contextMenuAtendimento?.totalMensagensNaoLidas === -1;
  const contextMenuIsMarkedAsUnread =
    contextMenuAtendimento?.totalMensagensNaoLidas === -1;

  return (
    <Grid
      item
      xs={4}
      sm={4}
      md={4}
      lg={3}
      component={Box}
      height="100%"
      overflow="hidden"
      hidden={atendimentosIniciados.length === 0 && !filterOptions.search}
      className={clsx({
        [classes.grid]: !(
          atendimentosIniciados.length === 0 && !filterOptions.search
        ),
      })}
    >
      {!currentUser?.disponivel && (
        <Box mb={2}>
          <AlertaIndisponibilidade />
        </Box>
      )}

      {(atendimentosIniciados.length > 0 ||
        (atendimentosIniciados.length === 0 && filterOptions.search)) && (
        <Box marginBottom={1}>
          <Box display="flex" alignItems="center" gridGap={8}>
            <Box flexGrow={1}>
              <SearchBar
                placeholder="Nome do contato ou mensagem"
                onChange={handleSearch}
                onCancelSearch={handleCancelSearch}
              />
            </Box>

            <FilterListaAtendimentos
              filteredOptions={filterOptions}
              handleSetOrderBy={handleSetOrderBy}
              onMarkAllAsRead={handleMarkAsRead}
            />
          </Box>
        </Box>
      )}

      <List className={classes.list} disablePadding dense ref={parentRef}>
        {atendimentosIniciados.length > 0 ? (
          <VirtualizedListContainer
            virtualizer={virtualizer}
            virtualItems={virtualItems}
          >
            {renderAtendimentosIniciados}
          </VirtualizedListContainer>
        ) : (
          filterOptions.search && (
            <ListItem disableGutters dense>
              <ListItemText
                primary="Tem certeza que digitou certo!?"
                secondary={`Não encontramos nenhum contato ou atendimento com ${filterOptions.search}`}
                primaryTypographyProps={{
                  variant: 'body2',
                  align: 'center',
                  style: { marginTop: 8 },
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  align: 'center',
                  style: { marginTop: 4, paddingInline: 16 },
                }}
              />
            </ListItem>
          )
        )}
      </List>

      <CardContextMenu
        position={contextMenuPosition}
        paperRef={menuPaperRef}
        hasUnread={contextMenuHasUnread}
        isMarkedAsUnread={contextMenuIsMarkedAsUnread}
        onMarkAsRead={() =>
          contextMenuAtendimento &&
          handleMarcarComoLido(contextMenuAtendimento.id)
        }
        onMarkAsUnread={() =>
          contextMenuAtendimento &&
          handleMarcarComoNaoLido(contextMenuAtendimento.id)
        }
      />
    </Grid>
  );
}
